import React, { useState, useRef, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import Webcam from 'react-webcam';
import { 
  Button, 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  IconButton,
  CircularProgress,
  FormHelperText
} from '@mui/material';
import { styled } from '@mui/material/styles';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid';
import DeleteIcon from '@mui/icons-material/Delete';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { isMobileDevice, getMobileFileAccept, isIOS as detectIOS, isWebView, isIOSWebView } from '../utils';

// Styled components
const CaptureContainer = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  position: 'relative',
  overflow: 'hidden'
}));

const WebcamContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  '& video': {
    width: '100%',
    height: 'auto',
    borderRadius: theme.shape.borderRadius,
  }
}));

const PreviewImage = styled('img')({
  width: '100%',
  height: 'auto',
  objectFit: 'contain',
  borderRadius: '4px'
});

const InputFileHidden = styled('input')({
  display: 'none'
});

const PhotoCapture = forwardRef(({ 
  onImageCapture, 
  label = "Photo", 
  required = false,
  aspectRatio = "4:3",
  maxSize = 1572864, // 1.5MB default
  allowedTypes = ["image/jpeg", "image/png", "image/webp"],
  onValidationChange = null,
  showValidationErrors = false
}, ref) => {
  const [image, setImage] = useState(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [facingMode, setFacingMode] = useState("user");
  const [cameraError, setCameraError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [validationError, setValidationError] = useState('');  const [isMobile, setIsMobile] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isInWebView, setIsInWebView] = useState(false);
  const [isInIOSWebView, setIsInIOSWebView] = useState(false);const webcamRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
    // Expose validation method via ref
  useImperativeHandle(ref, () => ({
    validate: () => validate(),
    isValid: () => {
      const error = validate(false);
      return error === null;
    }
  }));  // Detect mobile device
  useEffect(() => {
    setIsMobile(isMobileDevice());
    setIsIOS(detectIOS());
    setIsInWebView(isWebView());
    setIsInIOSWebView(isIOSWebView());
  }, []);
  
  // Calculate aspect ratio
  const getAspectRatio = () => {
    const [width, height] = aspectRatio.split(':').map(Number);
    return { width, height };
  };
  
  const { width, height } = getAspectRatio();
  const videoConstraints = {
    width: 1280,
    height: 720,
    facingMode: facingMode,
    aspectRatio: width / height
  };
  // Validation function
  const validate = useCallback((setErrors = true) => {
    if (required && !image) {
      const error = `Please provide a photo for ${label}`;
      if (setErrors && showValidationErrors) {
        setValidationError(error);
      }
      return error;
    }
    
    if (setErrors && showValidationErrors) {
      setValidationError('');
    }
    return null;
  }, [required, image, label, showValidationErrors]);
  // Update validation state when image changes or showValidationErrors changes
  useEffect(() => {
    if (onValidationChange) {
      const isValid = validate(false) === null;
      onValidationChange(isValid);
    }
    
    // Only update the error message if we're showing validation errors
    if (showValidationErrors) {
      validate();
    }
  }, [image, showValidationErrors, onValidationChange, validate]);
  // Cleanup camera resources when component unmounts
  useEffect(() => {
    // Store current ref value for cleanup
    const currentWebcamRef = webcamRef.current;
    
    return () => {
      // Clean up camera resources when component unmounts
      if (currentWebcamRef?.stream) {
        const stream = currentWebcamRef.stream;
        if (stream) {
          const tracks = stream.getTracks();
          tracks.forEach(track => track.stop());
        }
      }
    };
  }, []);

  // Toggle camera facing mode (front/back)
  const toggleFacingMode = useCallback(() => {
    setFacingMode(prevMode => prevMode === 'user' ? 'environment' : 'user');
  }, []);

  // Capture image from webcam
  const captureImage = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setImage(imageSrc);
      if (onImageCapture) {
        onImageCapture(imageSrc);
      }
      setIsCameraOpen(false);
    }
  }, [webcamRef, onImageCapture]);  // Handle file upload (including camera capture on mobile)
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    
    if (!file) return;
    
    // Get mobile-optimized allowed types
    const mobileAllowedTypes = getMobileFileAccept(allowedTypes);
      // Validate file type (more lenient on mobile for HEIC/HEIF support)
    if (!mobileAllowedTypes.includes(file.type)) {
      alert(`File type not supported. Please upload ${allowedTypes.join(' or ')}`);
      return;
    }
    
    // Check if file needs compression if it's too large
    if (file.size > maxSize) {
      // Try to compress the image to fit within maxSize for both mobile and desktop
      compressImageFile(file, maxSize);
      return;
    }
    
    setIsLoading(true);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const imageSrc = e.target.result;
      setImage(imageSrc);
      if (onImageCapture) {
        onImageCapture(imageSrc);
      }
      setIsLoading(false);
    };
    reader.onerror = () => {
      setIsLoading(false);
      alert('Error reading file');
    };
    reader.readAsDataURL(file);
    
    // Reset the input value to allow selecting the same file again
    event.target.value = '';
  };

  // Compress image file for both mobile and desktop devices
  const compressImageFile = (file, targetSize) => {
    setIsLoading(true);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Calculate new dimensions (same for both mobile and desktop)
        let { width, height } = img;
        const maxDimension = 1920; // Same max dimension for all devices
        
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height * maxDimension) / width;
            width = maxDimension;
          } else {
            width = (width * maxDimension) / height;
            height = maxDimension;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        
        // Start with reasonable quality and reduce if needed
        let quality = 0.8;
        let compressedDataUrl;
        
        do {
          compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          quality -= 0.1;
        } while (compressedDataUrl.length * 0.75 > targetSize && quality > 0.1); // Approximate base64 to bytes conversion
        
        if (compressedDataUrl.length * 0.75 <= targetSize * 1.2) { // Allow slight tolerance
          setImage(compressedDataUrl);
          if (onImageCapture) {
            onImageCapture(compressedDataUrl);
          }
          setIsLoading(false);
        } else {
          setIsLoading(false);
          setValidationError(`Image is too large even after compression. Please try a different photo or use a smaller image.`);
        }
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };
  // Clear captured image
  const clear = () => {
    setImage(null);
    if (onImageCapture) {
      onImageCapture(null);
    }
  };  // Toggle camera on/off
  const toggleCamera = async () => {
    // Check if browser supports getUserMedia when turning camera on
    if (!isCameraOpen) {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true });
        setIsCameraOpen(true);
        setCameraError(false);
      } catch (err) {
        setCameraError(true);
        console.error("Camera access error:", err);
      }
    } else {
      setIsCameraOpen(false);
      // Stop camera stream when closing
      if (webcamRef.current?.stream) {
        const stream = webcamRef.current.stream;
        if (stream) {
          const tracks = stream.getTracks();
          tracks.forEach(track => track.stop());
        }
      }
    }
  };  // Trigger file input click
  const triggerFileInput = useCallback(() => {
    fileInputRef.current.click();
  }, []);
  // Trigger camera input click (mobile optimized)
  const triggerCameraInput = useCallback(() => {
    cameraInputRef.current.click();
  }, []);

  return (
    <Box>
      <Typography 
        variant="subtitle1" 
        gutterBottom
        sx={{ 
          display: 'flex', 
          alignItems: 'center' 
        }}
      >
        {label} {required && <Typography color="error" component="span" ml={0.5}>*</Typography>}
      </Typography>
      
      <CaptureContainer elevation={1}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        ) : image ? (
          // Image preview
          <Box>
            <PreviewImage src={image} alt="Captured" />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button 
                startIcon={<DeleteIcon />}
                color="error" 
                onClick={clear}
              >
                Remove
              </Button>
            </Box>
          </Box>        ) : isCameraOpen && (!isMobile || isInWebView) ? (
          // Camera view (desktop or mobile WebView)
          <WebcamContainer>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              mirrored={facingMode === 'user'}
              onUserMediaError={(err) => {
                console.error("Webcam error:", err);
                setCameraError(true);
                setIsCameraOpen(false);
              }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
              <IconButton onClick={toggleFacingMode} color="primary">
                <FlipCameraAndroidIcon />
              </IconButton>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={captureImage}
                startIcon={<PhotoCameraIcon />}
              >
                Capture
              </Button>
            </Box>
          </WebcamContainer>) : (          // Buttons to either take photo or upload file
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {isInIOSWebView ? (
              // iOS WebView: Only file picker (native camera capture and webcam don't work reliably)
              <Grid item xs={12}>
                <InputFileHidden
                  accept={getMobileFileAccept(allowedTypes).join(',')}
                  id="file-input"
                  type="file"
                  onChange={handleFileUpload}
                  ref={fileInputRef}
                />
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<FileUploadIcon />}
                  onClick={triggerFileInput}
                  sx={{ mb: 2 }}
                >
                  Choose Photo
                </Button>
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                  To take a new photo, use your device's camera app first, then select the photo here.
                </Typography>
              </Grid>
            ) : isMobile && !isInWebView ? (
              // Mobile: Use native camera input and file upload (but not in WebViews)
              <>                {isIOS ? (
                  // iOS specific - single camera button defaulting to back camera
                  <Grid item xs={12}>
                    {/* Back Camera (default) */}
                    <InputFileHidden
                      accept="image/*"
                      capture="environment"
                      id="camera-input"
                      type="file"
                      onChange={handleFileUpload}
                      ref={cameraInputRef}
                    />                    
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<PhotoCameraIcon />}
                      onClick={triggerCameraInput}
                      sx={{ mb: 2 }}
                    >
                      Take Photo
                    </Button>                    
                    <InputFileHidden
                      accept={getMobileFileAccept(allowedTypes).join(',')}
                      id="file-input"
                      type="file"
                      onChange={handleFileUpload}
                      ref={fileInputRef}
                    />
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<FileUploadIcon />}
                      onClick={triggerFileInput}
                    >
                      Choose from Photo Library
                    </Button>
                  </Grid>) : (
                  // Android and other mobile devices
                  <Grid item xs={12}>
                    <InputFileHidden
                      accept="image/*"
                      capture="environment"
                      id="camera-input"
                      type="file"
                      onChange={handleFileUpload}
                      ref={cameraInputRef}
                    />                    
                    <Button
                      variant="contained"
                      fullWidth
                      startIcon={<PhotoCameraIcon />}
                      onClick={triggerCameraInput}
                      sx={{ mb: 1 }}
                    >
                      Take Photo
                    </Button>                    
                    <InputFileHidden
                      accept={getMobileFileAccept(allowedTypes).join(',')}
                      id="file-input"
                      type="file"
                      onChange={handleFileUpload}
                      ref={fileInputRef}
                    />                    
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<FileUploadIcon />}
                      onClick={triggerFileInput}
                    >
                      Choose from Photo Library
                    </Button>
                  </Grid>
                )}
              </>            ) : (
              // Desktop or WebView: Use webcam and file upload
              <>
                <Grid item xs={12} sm={6}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<PhotoCameraIcon />}
                    onClick={toggleCamera}
                    disabled={cameraError}
                  >
                    {cameraError ? 'Camera Not Available' : 'Take Photo'}
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <InputFileHidden
                    accept={allowedTypes.join(',')}
                    id="contained-button-file"
                    type="file"
                    onChange={handleFileUpload}
                    ref={fileInputRef}
                  />
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<FileUploadIcon />}
                    onClick={triggerFileInput}
                  >
                    Upload Photo
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        )}
      </CaptureContainer>
      
      {showValidationErrors && validationError && (
        <FormHelperText error>{validationError}</FormHelperText>
      )}
    </Box>
  );
});

export default PhotoCapture;

/*
 * PhotoCapture Component - Mobile Optimized with WebView Support
 * 
 * This component provides different experiences for mobile vs desktop:
 * 
 * MOBILE (iOS/Android) - Native Browser:
 * - Uses native HTML5 file input with capture="environment" for back camera
 * - Single "Take Photo" button that opens native camera interface
 * - "Choose from Photo Library" for gallery access
 * - Users can manually switch cameras using native camera controls
 * - Avoids react-webcam fullscreen issues on iOS
 * 
 * MOBILE - WebView (iOS/Android):
 * - Uses react-webcam for inline camera preview (same as desktop)
 * - WebViews often have permission issues with capture="environment"
 * - Provides webcam controls for face/environment camera switching
 * - Standard file upload option
 * 
 * DESKTOP:
 * - Uses react-webcam for inline camera preview
 * - Provides webcam controls for face/environment camera switching
 * - Standard file upload option
 * 
 * This approach ensures better UX across all platforms, especially handling
 * Android WebView permission issues where native camera capture fails.
 */